import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { API_BASE_URL } from '../config/env';
import { cacheClearAll } from '../services/api';

export const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// ---------------------------------------------------------------------------
// Google OAuth configuration — no Firebase SDK
// ---------------------------------------------------------------------------
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const REDIRECT_URI = `${window.location.origin}/auth/callback`;

// Generates a cryptographically random state value for CSRF protection
const generateState = () => {
  const arr = new Uint8Array(16);
  window.crypto.getRandomValues(arr);
  return Array.from(arr, b => b.toString(16).padStart(2, '0')).join('');
};

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Fetches the user's tree memberships and maps them into the `roles` shape
// that existing components expect: [{ treeId, role }]. Returns [] on failure.
const fetchUserRoles = async () => {
  try {
    const res = await fetch(`${API_BASE_URL}/me/trees`, {
      method: 'GET',
      credentials: 'include',
    });
    if (!res.ok) return [];
    const trees = await res.json();
    // /me/trees returns MyTreeDto with familyTreeId (not id)
    return Array.isArray(trees)
      ? trees
          .map(t => ({
            treeId: t.familyTreeId ?? t.id,
            role: t.role,
          }))
          .filter(r => r.treeId != null && r.role)
      : [];
  } catch {
    return [];
  }
};

const fetchSessionProfile = async ({ retries = 0, retryDelayMs = 250 } = {}) => {
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      const res = await fetch(`${API_BASE_URL}/me`, {
        method: 'GET',
        credentials: 'include',
      });

      if (res.ok) {
        const profile = await res.json();
        // /me is now zero-DB on the server and no longer returns roles. Hydrate
        // them from /me/trees so components that read user.roles keep working.
        const roles = await fetchUserRoles();
        return { ...profile, roles };
      }

      if (res.status === 401) {
        if (attempt < retries) {
          await delay(retryDelayMs);
          continue;
        }
        return null;
      }

      throw new Error('Unable to load your account. Please refresh and try again.');
    } catch (error) {
      if (attempt < retries) {
        await delay(retryDelayMs);
        continue;
      }
      throw error;
    }
  }

  return null;
};

/**
 * Exchanges a Google authorization code for an ID token, then calls our backend
 * to create a session. Returns the user profile on success.
 *
 * The code exchange happens server-side so the client_secret is never in the browser.
 */
const exchangeOAuthCallback = async (code, state) => {
  // Verify CSRF state
  const savedState = sessionStorage.getItem('oauth_state');
  sessionStorage.removeItem('oauth_state');
  if (!savedState || savedState !== state) {
    throw new Error('Invalid OAuth state. Please try logging in again.');
  }

  // Send the authorization code to our backend.
  // The backend exchanges it for an ID token using the client_secret (kept server-side),
  // verifies the token, and creates an HTTP session in one step.
  const res = await fetch(`${API_BASE_URL}/auth/google/callback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ code, redirectUri: REDIRECT_URI }),
  });

  if (!res.ok) {
    const status = res.status;
    const body = await res.json().catch(() => ({}));
    if (status >= 500) throw new Error('The server is temporarily unavailable. Please try again in a few minutes.');
    if (status === 429) throw new Error('Too many login attempts. Please wait a moment and try again.');
    if (status === 403) throw new Error('Login is not allowed for this account. Please contact support.');
    throw new Error(body.message || body.error || 'Authentication failed. Please try again.');
  }

  const profile = await res.json().catch(() => null);
  if (!profile || !profile.uid) {
    throw new Error('Login succeeded, but your profile could not be loaded. Please try again.');
  }

  // Discard anything cached for a previous session before loading this user's
  // data. Cache keys like `me:trees` are not per-user, so a stale empty list
  // would otherwise be replayed and this user told they have no trees.
  cacheClearAll();

  const roles = await fetchUserRoles();
  return { ...profile, roles };
};

// ---------------------------------------------------------------------------
// AuthProvider
// ---------------------------------------------------------------------------
export const AuthProvider = ({ children }) => {
  const [user, setUser]     = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async ({ retries = 0, retryDelayMs = 250 } = {}) => {
    try {
      const profile = await fetchSessionProfile({ retries, retryDelayMs });
      setUser(profile);
      return profile;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const completeOAuthCallback = useCallback(async (code, state) => {
    const profile = await exchangeOAuthCallback(code, state);
    setUser(profile);
    return profile;
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  /**
   * Initiates the Google OAuth flow by redirecting to Google's authorization endpoint.
   * The user will be returned to /auth/callback with ?code=... after consent.
   */
  const loginWithGoogle = useCallback(() => {
    if (!GOOGLE_CLIENT_ID) {
      throw new Error('REACT_APP_GOOGLE_CLIENT_ID is not configured');
    }
    const state = generateState();
    sessionStorage.setItem('oauth_state', state);

    const params = new URLSearchParams({
      client_id:     GOOGLE_CLIENT_ID,
      redirect_uri:  REDIRECT_URI,
      response_type: 'code',
      scope:         'openid email profile',
      access_type:   'offline',
      prompt:        'select_account',
      state,
    });

    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch(`${API_BASE_URL}/auth/sessionLogout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch { /* ignore */ }
    cacheClearAll();
    setUser(null);
  }, []);

  const value = {
    user,
    loading,
    authenticated: !!user,
    loginWithGoogle,
    logout,
    refreshUser: fetchMe,
    completeOAuthCallback,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
